import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { FileText, User, Calendar, Pencil, Copy, Send } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { getOptionLabel, REPORT_CATEGORIES } from "@/lib/reportOptionsData";
import { EditReportDialog } from "./EditReportDialog";
import { SendReportEmailDialog } from "./SendReportEmailDialog";

interface ViewReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  leadTitle?: string | null;
  /** When provided, shows the report for a single itinerary component instead of the programme wrap-up */
  itineraryItemId?: string | null;
  itineraryItemTitle?: string | null;
}


const getRatingColor = (categoryId: string, value: string): string => {
  // Positive ratings
  const positiveValues = ["on_time", "slightly_early", "exceptional", "good", "excellent", "very_satisfied", "satisfied", "none"];
  // Neutral ratings
  const neutralValues = ["slightly_late", "adequate", "average", "acceptable", "neutral", "minor_resolved"];
  // Negative ratings
  const negativeValues = ["significantly_late", "client_late", "below_expectations", "poor", "challenging", "would_not_repeat", "dissatisfied", "very_dissatisfied", "ongoing", "critical"];

  if (positiveValues.includes(value)) return "bg-green-500/10 text-green-700 dark:text-green-400";
  if (neutralValues.includes(value)) return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400";
  if (negativeValues.includes(value)) return "bg-red-500/10 text-red-700 dark:text-red-400";
  return "bg-muted text-muted-foreground";
};

export function ViewReportDialog({
  open,
  onOpenChange,
  leadId,
  leadTitle,
  itineraryItemId: itineraryItemIdProp,
  itineraryItemTitle: itineraryItemTitleProp,
}: ViewReportDialogProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [picked, setPicked] = useState<string | null | undefined>(undefined);

  // Opened from the lead (no specific activity): list every report on the programme.
  const { data: allReports } = useQuery({
    queryKey: ["lead-all-reports", leadId],
    enabled: open && !!leadId && !itineraryItemIdProp,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lead_reports")
        .select("id, itinerary_item_id, submitted_at, itinerary_items(title, custom_title, date)")
        .eq("lead_id", leadId)
        .order("submitted_at", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });
  const hasWrapUp = (allReports || []).some((r: any) => !r.itinerary_item_id);
  const componentList = (allReports || []).filter((r: any) => r.itinerary_item_id);
  const itineraryItemId: string | null | undefined = itineraryItemIdProp
    ?? (picked !== undefined ? picked : hasWrapUp ? null : (componentList[0] as any)?.itinerary_item_id ?? null);
  const pickedRow: any = componentList.find((r: any) => r.itinerary_item_id === itineraryItemId);
  const itineraryItemTitle = itineraryItemTitleProp
    ?? (pickedRow ? pickedRow.itinerary_items?.custom_title || pickedRow.itinerary_items?.title : undefined);
  const isComponentReport = !!itineraryItemId;

  const { data: report, isLoading: reportLoading } = useQuery({
    queryKey: isComponentReport
      ? ["lead-component-report-view", itineraryItemId]
      : ["lead-report", leadId],
    queryFn: async () => {
      let query = supabase
        .from("lead_reports")
        .select("*")
        .eq("lead_id", leadId);

      query = isComponentReport
        ? query.eq("itinerary_item_id", itineraryItemId as string)
        : query.is("itinerary_item_id", null);

      const { data, error } = await query.maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: open && !!leadId,
    staleTime: 0,
    refetchOnMount: 'always',
  });


  // Fetch guide supplier for the lead (guide is a supplier with category "Guide")
  const { data: guideSupplier } = useQuery({
    queryKey: ["lead-guide-supplier", leadId],
    queryFn: async () => {
      // First get the Guide category ID
      const { data: guideCategory } = await supabase
        .from("supplier_categories")
        .select("id")
        .ilike("name", "guide")
        .maybeSingle();
      
      if (!guideCategory) return null;
      
      // Then find the guide supplier for this lead
      const { data } = await supabase
        .from("lead_item_suppliers")
        .select("suppliers(name, contact_name)")
        .eq("lead_id", leadId)
        .eq("supplier_category_id", guideCategory.id)
        .maybeSingle();
      
      return data?.suppliers as { name: string | null; contact_name: string | null } | null;
    },
    enabled: open && !!leadId,
  });

  // Fetch primary tour item for the lead
  const { data: primaryTourItem } = useQuery({
    queryKey: ["lead-primary-tour", leadId],
    queryFn: async () => {
      const { data } = await supabase
        .from("itinerary_items")
        .select("title")
        .eq("lead_id", leadId)
        .in("item_type", ["tour", "activity"])
        .order("date", { ascending: true })
        .order("sort_order", { ascending: true })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: open && !!leadId,
  });

  const isLoading = reportLoading;

  const renderRatingBadge = (categoryId: string, value: string | null) => {
    if (!value) return <span className="text-muted-foreground text-sm">Not rated</span>;
    if (value === "n/a") return <Badge variant="secondary">N/A</Badge>;
    
    const label = getOptionLabel(categoryId, value);
    const colorClass = getRatingColor(categoryId, value);
    
    return <Badge className={colorClass}>{label}</Badge>;
  };

  const getCategoryLabel = (categoryId: string): string => {
    const category = REPORT_CATEGORIES.find((c) => c.id === categoryId);
    return category?.name || categoryId;
  };

  const getReportLink = () => {
    if (!report?.share_token) return "";
    return `${window.location.origin}/report/${report.share_token}`;
  };

  const handleCopyLink = () => {
    const link = getReportLink();
    if (!link) {
      toast({
        title: "Unable to copy link",
        description: "This report does not have a share token.",
        variant: "destructive",
      });
      return;
    }
    navigator.clipboard.writeText(link);
    toast({
      title: "Link copied",
      description: "The report link has been copied to your clipboard.",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {isComponentReport ? "Guide Report" : "Programme Report"}
            <Badge variant="outline" className={isComponentReport ? "text-xs bg-primary/10 text-primary border-primary/30" : "text-xs bg-secondary text-secondary-foreground"}>
              {isComponentReport ? "Single tour" : "Whole programme"}
            </Badge>
          </DialogTitle>
          <DialogDescription className="space-y-1">
            <div className="font-medium text-foreground">{leadTitle || "Tour Report"}</div>
            {isComponentReport ? (
              itineraryItemTitle && (
                <div className="text-sm font-medium text-primary">{itineraryItemTitle}</div>
              )
            ) : (
              primaryTourItem?.title && (
                <div className="text-sm font-medium text-primary">{primaryTourItem.title}</div>
              )
            )}

            <div className="flex items-center gap-3 text-sm pt-1">
              {guideSupplier && (
                <span className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  {guideSupplier.name || guideSupplier.contact_name || "Guide"}
                </span>
              )}
              {report?.submitted_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {format(new Date(report.submitted_at), "MMM dd, yyyy 'at' HH:mm")}
                </span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        {!itineraryItemIdProp && (allReports || []).length > 1 && (
          <div className="flex flex-wrap gap-2">
            {(allReports || []).map((r: any) => {
              const active = (r.itinerary_item_id ?? null) === (itineraryItemId ?? null);
              const label = r.itinerary_item_id
                ? (r.itinerary_items?.custom_title || r.itinerary_items?.title || "Activity")
                : "Overall programme";
              return (
                <Button key={r.id} size="sm" variant={active ? "default" : "outline"}
                  onClick={() => setPicked(r.itinerary_item_id ?? null)}>
                  {label}
                </Button>
              );
            })}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : !report ? (
          <div className="text-center py-8 text-muted-foreground">
            {isComponentReport ? "No report found for this component yet." : "No report found for this lead."}
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6">
              {/* Ratings Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{getCategoryLabel("timing")}</p>
                  {renderRatingBadge("timing", report.timing_rating)}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{getCategoryLabel("engagement")}</p>
                  {renderRatingBadge("engagement", report.engagement_rating)}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{getCategoryLabel("transport")}</p>
                  {renderRatingBadge("transport", report.transport_rating)}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{getCategoryLabel("satisfaction")}</p>
                  {renderRatingBadge("satisfaction", report.satisfaction_rating)}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{getCategoryLabel("guide_rating")}</p>
                  {renderRatingBadge("guide_rating", report.guide_rating)}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{getCategoryLabel("issues")}</p>
                  {renderRatingBadge("issues", report.issues_status)}
                </div>
              </div>

              {/* Client Feedback */}
              {report.client_feedback && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Client Feedback</p>
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <p className="text-sm italic">"{report.client_feedback}"</p>
                    </div>
                  </div>
                </>
              )}

              {/* Guide Feedback */}
              {report.guide_feedback && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Guide Feedback</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{report.guide_feedback}</p>
                  </div>
                </>
              )}

              {/* Additional Notes */}
              {report.additional_notes && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Additional Notes</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{report.additional_notes}</p>
                  </div>
                </>
              )}

              {/* Notes added after submission */}
              {Array.isArray(report.report_notes) && report.report_notes.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Notes Added After Submission</p>
                    {(report.report_notes as any[]).map((n: any) => (
                      <div key={n.id} className="rounded-lg border p-3 space-y-1">
                        <p className="text-sm whitespace-pre-wrap">{n.note}</p>
                        <p className="text-xs text-muted-foreground">
                          {n.created_by_name || "Team member"}
                          {n.created_at && ` • ${format(new Date(n.created_at), "MMM dd, yyyy 'at' HH:mm")}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        )}

        {/* Footer with share and edit buttons */}
        {report && (
          <div className="flex justify-between pt-4 border-t">
            <div className="flex gap-2">
              {!isComponentReport && (
                <>
                  <Button variant="outline" size="sm" onClick={handleCopyLink}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSendEmailOpen(true)}>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </Button>
                </>
              )}
            </div>

            <Button onClick={() => setEditDialogOpen(true)} variant="outline" size="sm">
              <Pencil className="h-4 w-4 mr-2" />
              Add Note
            </Button>
          </div>
        )}
      </DialogContent>

      {/* Add Note Dialog (original submission stays locked) */}
      {report && (
        <EditReportDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          reportId={report.id}
          table="lead_reports"
          leadId={leadId}
          title={leadTitle}
        />
      )}

      {/* Send Report Email Dialog */}
      {report && (
        <SendReportEmailDialog
          open={sendEmailOpen}
          onOpenChange={setSendEmailOpen}
          reportLink={getReportLink()}
          leadTitle={leadTitle}
          guideName={guideSupplier?.name || guideSupplier?.contact_name}
          leadId={leadId}
        />
      )}
    </Dialog>
  );
}
